import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, CheckCircle2, Send, Sparkles, Smartphone, Layers, Cpu, Flame, Loader2, ArrowRight
} from "lucide-react";

export default function AppPlanApply() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const planName = searchParams.get("plan") || "iOS Native App";

  const appPlanDetails: Record<string, { title: string; subTitle: string; priceINR: string; priceUSD: string; icon: any; features: string[]; techStack: string[] }> = {
    "iOS Native App": {
      title: "iOS Native App Plan",
      subTitle: "Swift / SwiftUI",
      priceINR: "₹35,000",
      priceUSD: "$499",
      icon: <Smartphone className="text-blue-600 dark:text-blue-400" size={24} />,
      features: ["Native Swift & SwiftUI Codebase", "Apple App Store Publishing", "Push Notification Setup", "Biometric Auth (Face/Touch ID)", "Cloud Sync & CoreData"],
      techStack: ["Swift", "SwiftUI", "Firebase"]
    },
    "Android Native App": {
      title: "Android Native App Plan",
      subTitle: "Kotlin / Jetpack",
      priceINR: "₹30,000",
      priceUSD: "$429",
      icon: <Layers className="text-blue-600 dark:text-blue-400" size={24} />,
      features: ["Native Kotlin & Jetpack Compose", "Google Play Console Submission", "Real-Time Push Alerts", "Google Pay & UPI Integration", "Background Sync Services"],
      techStack: ["Kotlin", "Jetpack", "Retrofit"]
    },
    "Cross-Platform Hybrid": {
      title: "Cross-Platform Hybrid Plan",
      subTitle: "Flutter / React Native",
      priceINR: "₹45,000",
      priceUSD: "$649",
      icon: <Cpu className="text-blue-600 dark:text-blue-400" size={24} />,
      features: ["Single Codebase Dual Store App", "iOS & Android Simultaneous Release", "Custom UI Animation Engine", "Off-line First Data Storage", "State Management (Bloc/Redux)"],
      techStack: ["Flutter", "React Native", "Dart"]
    },
    "Enterprise Mobile SaaS": {
      title: "Enterprise Mobile SaaS Plan",
      subTitle: "Full Cloud Ecosystem",
      priceINR: "₹65,000",
      priceUSD: "$949",
      icon: <Flame className="text-blue-600 dark:text-blue-400" size={24} />,
      features: ["High-Throughput Node/Go Backend", "Multi-Tenant Cloud Architecture", "End-to-End Encryption Security", "Dedicated Analytics Dashboard", "24/7 SLA Engineering Response"],
      techStack: ["Go", "Node.js", "Kubernetes"]
    }
  };

  const selectedPlan = appPlanDetails[planName] || appPlanDetails["iOS Native App"];

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    company: "",
    notes: ""
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email) return;

    setIsSubmitting(true);

    const payload = {
      type: `Mobile App Plan Application (${selectedPlan.title})`,
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      company: formData.company,
      service: `Mobile App Engineering: ${selectedPlan.title} (${selectedPlan.priceINR} / ${selectedPlan.priceUSD})`,
      notes: formData.notes
    };

    try {
      const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const mailEndpoint = isLocal ? "http://localhost:5000/api/send-mail" : "/api/send-mail.php";

      const response = await fetch(mailEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success !== false) {
        setSubmitted(true);
      } else {
        setSubmitted(true);
      }
    } catch (error) {
      try {
        await fetch("http://localhost:5000/api/send-mail", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
      } catch (err) {}
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-sans py-10 md:py-16 px-4 sm:px-6 transition-colors duration-300">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* BACK TO PRICING BUTTON */}
        <div>
          <button 
            onClick={() => navigate("/pricing")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 font-semibold text-xs transition-all shadow-sm cursor-pointer"
          >
            <ArrowLeft size={16} />
            <span>Back to Pricing Plans</span>
          </button>
        </div>

        {/* HEADER SECTION */}
        <header className="space-y-3 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={13} />
            <span className="text-xs font-bold uppercase tracking-wider">
              Mobile App Development Plan Application
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Apply for <span className="text-blue-600 dark:text-blue-400">{selectedPlan.title}</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">
            Order your mobile app project. Fill out the details below to receive direct project initiation support.
          </p>
        </header>

        {/* 2-COLUMN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* LEFT: PLAN SUMMARY CARD */}
          <div className="lg:col-span-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-6 space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 flex items-center justify-center">
                  {selectedPlan.icon}
                </div>
                <div>
                  <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">{selectedPlan.title}</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">{selectedPlan.subTitle}</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-slate-800/60 p-4 rounded-xl space-y-1">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">One-Time Project Price</span>
              <div className="text-2xl font-black text-blue-600 dark:text-blue-400">
                {selectedPlan.priceINR} <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">({selectedPlan.priceUSD})</span>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Tech Stack:</h4>
              <div className="flex flex-wrap gap-1.5">
                {selectedPlan.techStack.map((tech, idx) => (
                  <span key={idx} className="px-2.5 py-1 rounded-md bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 text-xs font-bold border border-blue-200 dark:border-blue-500/20">
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Plan Features:</h4>
              <ul className="space-y-2.5">
                {selectedPlan.features.map((feat, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                    <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* RIGHT: APPLICATION FORM */}
          <div className="lg:col-span-7 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-6 sm:p-8">
            {submitted ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10 space-y-4 max-w-md mx-auto"
              >
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-md">
                  <CheckCircle2 size={36} />
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900 dark:text-white">Application Received!</h3>
                <p className="text-slate-600 dark:text-slate-300 text-sm font-medium">
                  Thank you for applying for <strong>{selectedPlan.title}</strong>. Notification emails have been sent to <strong className="text-blue-600 dark:text-cyan-400">admin@hickter.com</strong> and a confirmation email has been sent to <strong className="text-blue-600 dark:text-cyan-400">{formData.email}</strong>.
                </p>
                <button 
                  onClick={() => setSubmitted(false)} 
                  className="btn-outline text-xs px-6 py-2.5 rounded-xl cursor-pointer mt-2"
                >
                  Submit Another Application
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} id="appPlanApplyForm" className="space-y-5">
                <h3 className="text-lg font-extrabold text-slate-900 dark:text-white pb-2 border-b border-slate-200 dark:border-slate-800">
                  Mobile App Application Form
                </h3>

                <div>
                  <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1 block">Full Name *</label>
                  <input 
                    type="text" 
                    required 
                    placeholder="John Doe"
                    value={formData.fullName} 
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:border-blue-600 dark:bg-slate-800 dark:border-slate-700 dark:text-white h-11 px-4 rounded-xl text-sm w-full outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1 block">Work Email *</label>
                    <input 
                      type="email" 
                      required 
                      placeholder="john@company.com"
                      value={formData.email} 
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:border-blue-600 dark:bg-slate-800 dark:border-slate-700 dark:text-white h-11 px-4 rounded-xl text-sm w-full outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1 block">Phone Number</label>
                    <input 
                      type="tel" 
                      placeholder="+91 98765 43210"
                      value={formData.phone} 
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:border-blue-600 dark:bg-slate-800 dark:border-slate-700 dark:text-white h-11 px-4 rounded-xl text-sm w-full outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1 block">Company Name</label>
                  <input 
                    type="text" 
                    placeholder="Acme Corp"
                    value={formData.company} 
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:border-blue-600 dark:bg-slate-800 dark:border-slate-700 dark:text-white h-11 px-4 rounded-xl text-sm w-full outline-none"
                  />
                </div>

                <div>
                  <label className="text-slate-700 dark:text-slate-200 text-sm font-semibold mb-1 block">Mobile App Specifications & Requirements</label>
                  <textarea 
                    placeholder="Provide details about app features, target platform, or reference apps..."
                    value={formData.notes} 
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-slate-50 border border-slate-300 text-slate-900 focus:bg-white focus:border-blue-600 dark:bg-slate-800 dark:border-slate-700 dark:text-white p-4 rounded-xl text-sm w-full min-h-[100px] outline-none"
                  />
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="hidden sm:flex w-full h-12 rounded-xl font-semibold text-base items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white shadow-md shadow-blue-600/25 cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin !text-white" />
                      <span className="!text-white">Submitting Application & Sending Emails...</span>
                    </>
                  ) : (
                    <>
                      <Send size={18} className="!text-white" />
                      <span className="!text-white">Apply Now & Build Mobile App</span>
                      <ArrowRight size={18} className="!text-white" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

        </div>

      </div>

      {/* MOBILE FIXED BOTTOM SUBMISSION BAR */}
      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 z-[120] bg-white/95 dark:bg-[#030712]/95 backdrop-blur-xl border-t border-slate-200 dark:border-slate-800 p-3 shadow-2xl block sm:hidden">
          <button
            type="submit"
            form="appPlanApplyForm"
            disabled={isSubmitting}
            className="w-full h-12 rounded-xl font-black text-sm bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-600 !text-white shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2.5 transition-all cursor-pointer disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin !text-white" />
                <span className="!text-white">Submitting Application...</span>
              </>
            ) : (
              <>
                <Send size={18} className="!text-white" />
                <span className="!text-white font-black tracking-wide">Apply Now & Build App</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
